import { Copy, CheckCircle2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import type { CreatedAbTestPayload } from "./create-test-modal.types";

interface TestSuccessDialogProps {
  open: boolean;
  payload: CreatedAbTestPayload | null;
  onClose: () => void;
}

export function TestSuccessDialog({ open, payload, onClose }: TestSuccessDialogProps) {
  const handleCopy = () => {
    if (!payload) return;
    const parts: string[] = [];
    if (payload.variantB.title) parts.push(payload.variantB.title);
    payload.variantB.bullets.filter(Boolean).forEach(b => parts.push(b));
    void navigator.clipboard.writeText(parts.join("\n\n"));
    toast({ title: "Copied", description: "Variant B content copied to clipboard." });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-[440px] max-h-[90vh] flex flex-col bg-card">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-trend-positive" />
            <DialogTitle>Test Created</DialogTitle>
          </div>
          <DialogDescription>
            Your A/B test is now created. Copy the Variant B content below and paste it into Amazon Manage Your Experiments.
          </DialogDescription>
        </DialogHeader>
        {payload && (
          <div className="space-y-3 py-2 min-h-0 overflow-y-auto">
            {payload.variantB.title && (
              <div className="space-y-1">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Title</p>
                <p className="text-sm leading-relaxed text-foreground bg-muted/50 rounded-md p-2.5">{payload.variantB.title}</p>
              </div>
            )}
            {payload.variantB.bullets.filter(Boolean).map((bullet, idx) => (
              <div key={idx} className="space-y-1">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Bullet {idx + 1}</p>
                <p className="text-sm leading-relaxed text-foreground bg-muted/50 rounded-md p-2.5">{bullet}</p>
              </div>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={handleCopy}>
            <Copy className="h-3.5 w-3.5" />
            Copy Variant B
          </Button>
          <Button onClick={onClose}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
